/**
 * Charts.
 *
 * Built as plain SVG rather than pulled from a charting library, because the
 * mark specifications here are precise and a library would fight them:
 *
 *   * Thin marks with 4px-rounded data-ends anchored to the baseline.
 *   * A 2px surface-coloured gap between adjacent fills, so segments read as
 *     separate marks rather than one blended block.
 *   * Recessive grid and axes — the data is the message, not the scaffolding.
 *   * Selective direct labels. Never a number on every mark.
 *   * A hover tooltip on every mark, with a hit target larger than the mark.
 *   * A legend whenever there are two or more series, plus direct labels when
 *     there are four or fewer — identity is never carried by colour alone.
 *   * A table view behind a disclosure, so the same data is available to a
 *     screen reader and to anyone who simply wants the numbers.
 *
 * Colours come from CSS custom properties, so light and dark are each a
 * deliberately chosen palette rather than an automatic inversion.
 */

import { useId, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface Datum {
  /** Stable key — also the colour key, so a filter cannot repaint survivors. */
  key: string;
  label: string;
  value: number;
  /** A reserved status colour, when the datum genuinely represents a status. */
  tone?: 'good' | 'warning' | 'serious' | 'critical' | 'neutral';
  /** Otherwise a categorical slot, 1–8, assigned in fixed order. */
  series?: number;
}

/** Resolve a datum to a CSS colour. Status colours win when present. */
function colorFor(d: Datum, index: number): string {
  if (d.tone) {
    return d.tone === 'neutral' ? 'rgb(var(--text-muted))' : `rgb(var(--status-${d.tone}))`;
  }
  // Fixed-order assignment, never cycled past slot 8 — see below.
  const slot = d.series ?? index + 1;
  return `rgb(var(--series-${Math.min(slot, 8)}))`;
}

/**
 * Horizontal bar chart.
 *
 * Horizontal rather than vertical because these categories have long names
 * ("Needs improvement", "Not started") — vertical bars would force rotated
 * labels, which are measurably slower to read.
 */
export function BarChart({ data, title, total, emptyLabel = 'No data', valueSuffix = '' }: {
  data: Datum[];
  title?: string;
  /** Denominator for the bar scale. Defaults to the largest value. */
  total?: number;
  emptyLabel?: string;
  valueSuffix?: string;
}) {
  const [hovered, setHovered] = useState<string | null>(null);
  const [showTable, setShowTable] = useState(false);
  const tableId = useId();

  const nonZero = data.filter((d) => d.value > 0);
  const max = total ?? Math.max(...data.map((d) => d.value), 1);
  const sum = data.reduce((s, d) => s + d.value, 0);

  if (nonZero.length === 0) {
    return (
      <div>
        {title && <p className="text-sm font-medium text-ink-secondary mb-3">{title}</p>}
        <p className="text-sm text-ink-muted py-6 text-center">{emptyLabel}</p>
      </div>
    );
  }

  return (
    <div>
      {title && <p className="text-sm font-medium text-ink-secondary mb-3">{title}</p>}

      <ul className="space-y-2.5">
        {data.map((d, i) => {
          const pct = max > 0 ? (d.value / max) * 100 : 0;
          const isHovered = hovered === d.key;
          return (
            <li
              key={d.key}
              // The hit target is the whole row, which is far larger than the
              // 8px-tall mark — essential on touch.
              className="group relative cursor-default"
              onMouseEnter={() => setHovered(d.key)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setHovered(d.key)}
              onBlur={() => setHovered(null)}
              tabIndex={0}
            >
              <div className="flex items-baseline justify-between gap-3 mb-1">
                {/* Direct label — identity never rests on the colour swatch. */}
                <span className="text-sm text-ink-secondary truncate flex items-center gap-2">
                  <span
                    aria-hidden
                    className="w-2.5 h-2.5 rounded-sm shrink-0"
                    style={{ background: colorFor(d, i) }}
                  />
                  {d.label}
                </span>
                <span className="text-sm font-medium tabular-nums text-ink shrink-0">
                  {d.value}
                  {valueSuffix}
                </span>
              </div>
              <div className="h-2 rounded-full bg-ink/[0.06] overflow-hidden">
                <div
                  className={cn(
                    'h-full rounded-full transition-all duration-500',
                    isHovered && 'brightness-110',
                  )}
                  style={{ width: `${Math.max(pct, d.value > 0 ? 2 : 0)}%`, background: colorFor(d, i) }}
                />
              </div>
              {/* Hover detail: the share of the whole, which the bar alone does
                  not state. */}
              {isHovered && sum > 0 && (
                <div className="absolute right-0 -top-1 translate-y-[-100%] z-10
                                rounded-lg bg-surface-raised border border-line shadow-raised
                                px-2.5 py-1.5 text-xs whitespace-nowrap pointer-events-none">
                  <span className="text-ink font-medium">{d.value}</span>
                  <span className="text-ink-muted"> of {sum} · {Math.round((d.value / sum) * 100)}%</span>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <TableDisclosure
        id={tableId}
        open={showTable}
        onToggle={() => setShowTable((v) => !v)}
        headers={['Category', 'Value', 'Share']}
        rows={data.map((d) => [
          d.label,
          String(d.value),
          sum > 0 ? `${Math.round((d.value / sum) * 100)}%` : '—',
        ])}
      />
    </div>
  );
}

/**
 * Donut chart with a hero number in the middle.
 *
 * Used only where the parts genuinely sum to a meaningful whole (a status
 * breakdown does; unrelated metrics do not). The centre carries the total, so
 * the chart answers "how many altogether" without anyone estimating arc lengths.
 */
export function DonutChart({ data, centerValue, centerLabel, size = 168 }: {
  data: Datum[];
  centerValue: ReactNode;
  centerLabel?: string;
  size?: number;
}) {
  const [hovered, setHovered] = useState<string | null>(null);
  const [showTable, setShowTable] = useState(false);
  const tableId = useId();

  const sum = data.reduce((s, d) => s + d.value, 0);
  const radius = size / 2 - 14;
  const circumference = 2 * Math.PI * radius;
  // A 2px visual gap between adjacent arcs, expressed in path units.
  const gap = sum > 0 ? (2 / circumference) * 100 : 0;

  let offset = 0;

  return (
    // CONTAINER query, not a viewport breakpoint.
    //
    // This had two bugs. The table disclosure below was a THIRD child of the
    // same flex row, so at `xl` it sat beside the legend instead of under it and
    // the two overlapped. And `xl:flex-row` asked the wrong question: these
    // charts render four-across (`xl:grid-cols-4`), so at `xl` each CARD is a
    // quarter of the screen — narrow, exactly when the viewport says "wide".
    // The comment that used to be here warned about that trap for `sm` and then
    // repeated it at `xl`.
    //
    // `@container` measures the card itself, so the layout is right at any grid
    // density and for longer labels in Greek or German.
    <div className="oc-chart-container">
      <div className="oc-chart-row flex flex-col items-center gap-5">
        <div className="relative shrink-0" style={{ width: size, height: size }}>
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
               aria-label={centerLabel ?? 'Breakdown'}>
            {/* Recessive track, so an empty chart still reads as a chart. */}
            <circle
              cx={size / 2} cy={size / 2} r={radius}
              fill="none" stroke="rgb(var(--text-primary) / 0.06)" strokeWidth="14"
            />
            {sum > 0 &&
              data.filter((d) => d.value > 0).map((d, i) => {
                const share = (d.value / sum) * 100;
                const dash = Math.max(0, share - gap);
                const el = (
                  <circle
                    key={d.key}
                    cx={size / 2} cy={size / 2} r={radius}
                    fill="none"
                    stroke={colorFor(d, i)}
                    strokeWidth={hovered === d.key ? 18 : 14}
                    strokeDasharray={`${(dash / 100) * circumference} ${circumference}`}
                    strokeDashoffset={-(offset / 100) * circumference}
                    strokeLinecap="round"
                    // Rotate so the first segment starts at twelve o'clock.
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                    className="transition-all duration-300 cursor-default"
                    onMouseEnter={() => setHovered(d.key)}
                    onMouseLeave={() => setHovered(null)}
                  />
                );
                offset += share;
                return el;
              })}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-3xl font-semibold tabular-nums text-ink">{centerValue}</span>
            {centerLabel && <span className="text-xs text-ink-muted mt-0.5">{centerLabel}</span>}
          </div>
        </div>

        {/* Legend — always present for two or more series. */}
        <ul className="flex-1 w-full space-y-1.5 min-w-0">
          {data.map((d, i) => (
            <li
              key={d.key}
              className={cn(
                'flex items-center justify-between gap-3 text-sm rounded-lg px-2 py-1 -mx-2 transition-colors',
                hovered === d.key && 'bg-surface-sunken',
              )}
              onMouseEnter={() => setHovered(d.key)}
              onMouseLeave={() => setHovered(null)}
            >
              <span className="flex items-start gap-2 text-ink-secondary min-w-0">
                <span
                  aria-hidden
                  className="w-2.5 h-2.5 rounded-sm shrink-0 mt-1"
                  style={{ background: colorFor(d, i) }}
                />
                {/* Wraps: a clipped category name defeats the point of a legend. */}
                <span className="leading-snug">{d.label}</span>
              </span>
              <span className="tabular-nums font-medium text-ink shrink-0">{d.value}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Always BELOW the chart and legend, never a flex sibling of them. */}
      <div className="w-full">
        <TableDisclosure
          id={tableId}
          open={showTable}
          onToggle={() => setShowTable((v) => !v)}
          headers={['Category', 'Value']}
          rows={data.map((d) => [d.label, String(d.value)])}
        />
      </div>
    </div>
  );
}

/**
 * The table view every chart carries.
 *
 * Not an accessibility afterthought — sometimes the honest answer to "what does
 * this chart say" is a list of numbers, and people should be able to get at it.
 */
function TableDisclosure({ id, open, onToggle, headers, rows }: {
  id: string;
  open: boolean;
  onToggle: () => void;
  headers: string[];
  rows: string[][];
}) {
  return (
    <div className="mt-3">
      <button
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={id}
        className="text-xs text-ink-muted hover:text-ink-secondary underline underline-offset-2"
      >
        {open ? 'Hide data table' : 'View as table'}
      </button>
      {open && (
        <div id={id} className="oc-table-scroll mt-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line">
                {headers.map((h) => (
                  <th key={h} className="text-left py-1.5 px-2 font-medium text-ink-secondary">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="border-b border-line/50 last:border-0">
                  {row.map((cell, j) => (
                    <td key={j} className={cn('py-1.5 px-2 text-ink', j > 0 && 'tabular-nums')}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Scatter plot for vendor risk.
 *
 * Inherent risk on X, composite on Y, both on the same 0–3 scale — which lets
 * a **diagonal reference line** carry the real message: a point ON the line is
 * a supplier whose measured evidence has told you nothing you did not already
 * assume, and a point BELOW it is one your evidence has actually pulled down.
 * That comparison is the entire reason this is a scatter rather than a bar
 * chart of composite scores.
 *
 * Points are coloured by risk band from the reserved status ramp, never from
 * the categorical series colours — a risk band is a status. Each point also
 * carries a label on hover and every vendor appears in the table view, so the
 * banding is never conveyed by colour alone.
 */
export interface RiskPoint {
  id: string;
  name: string;
  /** 0–3. */
  x: number;
  /** 0–3. */
  y: number;
  band: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  /** How many of the two measurable inputs were actually available. */
  measured: number;
}

export function RiskScatter({ points, max = 3, onSelect }: {
  points: RiskPoint[];
  max?: number;
  onSelect?: (id: string) => void;
}) {
  const [hovered, setHovered] = useState<string | null>(null);
  const [showTable, setShowTable] = useState(false);
  const tableId = useId();

  // A square plot, because both axes carry the same scale and unit — an
  // unequal aspect would make the diagonal misleading.
  const size = 320;
  const pad = 34;
  const plot = size - pad * 2;

  const toX = (v: number) => pad + (Math.min(v, max) / max) * plot;
  const toY = (v: number) => size - pad - (Math.min(v, max) / max) * plot;

  const bandTone: Record<RiskPoint['band'], string> = {
    LOW: 'rgb(var(--status-good))',
    MEDIUM: 'rgb(var(--status-warning))',
    HIGH: 'rgb(var(--status-serious))',
    CRITICAL: 'rgb(var(--status-critical))',
  };

  if (points.length === 0) {
    return <p className="text-sm text-ink-muted py-8 text-center">No risk data yet.</p>;
  }

  const active = points.find((p) => p.id === hovered);

  return (
    <div>
      <div className="relative overflow-x-auto">
        <svg
          viewBox={`0 0 ${size} ${size}`}
          className="w-full max-w-[380px] mx-auto"
          role="img"
          aria-label="Vendor risk: inherent against composite"
        >
          {/* Recessive grid. The data is the message; the scaffolding is not. */}
          {[0, 1, 2, 3].map((v) => (
            <g key={v}>
              <line
                x1={toX(v)} y1={pad} x2={toX(v)} y2={size - pad}
                stroke="rgb(var(--text-primary) / 0.07)" strokeWidth="1"
              />
              <line
                x1={pad} y1={toY(v)} x2={size - pad} y2={toY(v)}
                stroke="rgb(var(--text-primary) / 0.07)" strokeWidth="1"
              />
              <text
                x={toX(v)} y={size - pad + 14} textAnchor="middle"
                className="fill-[rgb(var(--text-muted))]" fontSize="9"
              >{v}</text>
              <text
                x={pad - 8} y={toY(v) + 3} textAnchor="end"
                className="fill-[rgb(var(--text-muted))]" fontSize="9"
              >{v}</text>
            </g>
          ))}

          {/* The reference diagonal: composite == inherent. */}
          <line
            x1={toX(0)} y1={toY(0)} x2={toX(max)} y2={toY(max)}
            stroke="rgb(var(--text-primary) / 0.18)" strokeWidth="1.5" strokeDasharray="4 4"
          />

          {/* Axis titles */}
          <text
            x={size / 2} y={size - 4} textAnchor="middle"
            className="fill-[rgb(var(--text-secondary))]" fontSize="10"
          >Inherent risk</text>
          <text
            x={11} y={size / 2} textAnchor="middle" fontSize="10"
            transform={`rotate(-90 11 ${size / 2})`}
            className="fill-[rgb(var(--text-secondary))]"
          >Composite risk</text>

          {points.map((p) => {
            const isActive = hovered === p.id;
            return (
              <g key={p.id}>
                {/* An invisible, generous hit target: the visible mark is 7px,
                    which is far too small to tap reliably. */}
                <circle
                  cx={toX(p.x)} cy={toY(p.y)} r={16} fill="transparent"
                  className={onSelect ? 'cursor-pointer' : 'cursor-default'}
                  onMouseEnter={() => setHovered(p.id)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => onSelect?.(p.id)}
                />
                <circle
                  cx={toX(p.x)} cy={toY(p.y)} r={isActive ? 9 : 7}
                  fill={bandTone[p.band]}
                  // A surface-coloured ring separates overlapping points so two
                  // suppliers at similar scores still read as two marks.
                  stroke="rgb(var(--surface-2))" strokeWidth="2"
                  className="transition-all pointer-events-none"
                  // A vendor scored on judgement alone is drawn hollow, so a
                  // confident-looking dot can be told from an assumption.
                  fillOpacity={p.measured === 0 ? 0.35 : 1}
                />
              </g>
            );
          })}
        </svg>

        {active && (
          <div className="absolute top-2 left-2 rounded-lg bg-surface-raised border border-line
                          shadow-raised px-3 py-2 text-xs pointer-events-none max-w-[60%]">
            <p className="font-medium text-ink truncate">{active.name}</p>
            <p className="text-ink-secondary tabular-nums mt-0.5">
              inherent {active.x} → composite {active.y}
            </p>
            <p className="text-ink-muted mt-0.5">
              {active.measured}/2 inputs measured
            </p>
          </div>
        )}
      </div>

      <TableDisclosure
        id={tableId}
        open={showTable}
        onToggle={() => setShowTable((v) => !v)}
        headers={['Vendor', 'Inherent', 'Composite', 'Band', 'Measured']}
        rows={points.map((p) => [
          p.name,
          String(p.x),
          String(p.y),
          p.band,
          `${p.measured}/2`,
        ])}
      />
    </div>
  );
}

/**
 * Compliance trend over time.
 *
 * A single-series area chart: one measured number (overall compliance) against
 * a date axis. Built to the same mark rules as the rest of this file —
 *
 *   * A thin 2px line with an AREA FILL beneath it (a soft gradient to the
 *     baseline), because the message here is "a level that rose", and a filled
 *     area reads as a level where a bare line reads as a path.
 *   * An EMPHASISED ENDPOINT: the latest point is a larger filled dot with its
 *     value printed beside it, because "where we are now" is the number a CISO
 *     is actually asked for.
 *   * Recessive grid and axis; selective labels (first and last date only,
 *     never one per point); a hover tooltip on every point with a hit target
 *     far larger than the 3px mark; and the table view every chart carries.
 *
 * SEED points (back-dated samples derived from the current figure on a fresh
 * install) are drawn HOLLOW and called out in the table, so a derived sample is
 * never passed off as a day we measured. Unmeasured days are simply absent —
 * the line connects what was measured and does not invent the rest.
 *
 * Colour: the line uses categorical slot 1 (identity, not a verdict) — a single
 * trend line does not mean "good" or "bad" at any point; the value is labelled,
 * so meaning never rests on the colour.
 */
export interface TrendDatum {
  key: string;
  /** Short axis label, e.g. a date. */
  label: string;
  /** 0..max, or null for an unmeasured day (skipped). */
  value: number | null;
  /** Longer label for the tooltip/table, e.g. a full date. */
  longLabel?: string;
  /** A derived back-dated sample rather than a measured day. */
  seed?: boolean;
}

export function TrendArea({ data, title, max = 100, valueSuffix = '%', emptyLabel = 'No history yet' }: {
  data: TrendDatum[];
  title?: string;
  max?: number;
  valueSuffix?: string;
  emptyLabel?: string;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const [showTable, setShowTable] = useState(false);
  const tableId = useId();
  const gradId = useId();

  const measured = data.filter((d) => d.value !== null);

  if (measured.length === 0) {
    return (
      <div>
        {title && <p className="text-sm font-medium text-ink-secondary mb-3">{title}</p>}
        <p className="text-sm text-ink-muted py-10 text-center">{emptyLabel}</p>
      </div>
    );
  }

  // viewBox geometry. A wide aspect, because time is the long axis.
  const W = 640;
  const H = 220;
  const padL = 34;
  const padR = 16;
  const padT = 14;
  const padB = 28;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  // Even horizontal spacing by index across ALL data points (so gaps in
  // measurement still advance along the time axis).
  const n = data.length;
  const toX = (i: number) => (n <= 1 ? padL + plotW / 2 : padL + (i / (n - 1)) * plotW);
  const toY = (v: number) => padT + plotH - (Math.min(Math.max(v, 0), max) / max) * plotH;

  // Build the line through measured points only.
  const pts = data
    .map((d, i) => ({ d, i }))
    .filter((p) => p.d.value !== null)
    .map((p) => ({ x: toX(p.i), y: toY(p.d.value as number), ...p }));

  const linePath = pts.map((p, k) => `${k === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const areaPath =
    `M ${pts[0]!.x.toFixed(1)} ${(padT + plotH).toFixed(1)} ` +
    pts.map((p) => `L ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ') +
    ` L ${pts[pts.length - 1]!.x.toFixed(1)} ${(padT + plotH).toFixed(1)} Z`;

  const endpoint = pts[pts.length - 1]!;
  const lineColor = 'rgb(var(--series-1))';

  const gridValues = [0, max / 2, max];
  const firstLabel = data[0]?.label ?? '';
  const lastLabel = data[n - 1]?.label ?? '';

  return (
    <div>
      {title && <p className="text-sm font-medium text-ink-secondary mb-3">{title}</p>}

      <div className="relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full"
          role="img"
          aria-label={title ?? 'Compliance over time'}
        >
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={lineColor} stopOpacity="0.22" />
              <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Recessive horizontal grid + y labels (0, half, max). */}
          {gridValues.map((v) => (
            <g key={v}>
              <line
                x1={padL} y1={toY(v)} x2={W - padR} y2={toY(v)}
                stroke="rgb(var(--text-primary) / 0.07)" strokeWidth="1"
              />
              <text
                x={padL - 6} y={toY(v) + 3} textAnchor="end"
                className="fill-[rgb(var(--text-muted))]" fontSize="10"
              >{Math.round(v)}</text>
            </g>
          ))}

          {/* Area fill, then the line on top. */}
          <path d={areaPath} fill={`url(#${gradId})`} />
          <path d={linePath} fill="none" stroke={lineColor} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

          {/* Date labels: first and last only — never one per mark. */}
          <text x={toX(0)} y={H - 8} textAnchor="start" className="fill-[rgb(var(--text-muted))]" fontSize="10">{firstLabel}</text>
          {n > 1 && (
            <text x={toX(n - 1)} y={H - 8} textAnchor="end" className="fill-[rgb(var(--text-muted))]" fontSize="10">{lastLabel}</text>
          )}

          {pts.map((p, k) => {
            const isEnd = k === pts.length - 1;
            const isHover = hovered === p.i;
            return (
              <g key={p.d.key}>
                {/* Generous invisible hit target. */}
                <circle
                  cx={p.x} cy={p.y} r={18} fill="transparent"
                  className="cursor-default"
                  onMouseEnter={() => setHovered(p.i)}
                  onMouseLeave={() => setHovered(null)}
                />
                <circle
                  cx={p.x} cy={p.y}
                  r={isEnd ? 5 : isHover ? 4.5 : 3}
                  // Seed points are hollow; measured points are filled.
                  fill={p.d.seed ? 'rgb(var(--surface-2))' : lineColor}
                  stroke={lineColor}
                  strokeWidth={p.d.seed ? 1.5 : isEnd ? 2 : 1}
                  className="transition-all pointer-events-none"
                />
                {isEnd && (
                  <text
                    x={p.x - 8} y={p.y - 9} textAnchor="end"
                    className="fill-[rgb(var(--text-primary))]" fontSize="13" fontWeight="600"
                  >{p.d.value}{valueSuffix}</text>
                )}
              </g>
            );
          })}
        </svg>

        {hovered !== null && data[hovered]?.value !== null && (
          <div className="absolute top-1 left-10 rounded-lg bg-surface-raised border border-line
                          shadow-raised px-3 py-2 text-xs pointer-events-none max-w-[70%]">
            <p className="font-medium text-ink">
              {data[hovered]!.value}{valueSuffix}
              {data[hovered]!.seed && <span className="text-ink-muted font-normal"> · seed</span>}
            </p>
            <p className="text-ink-muted mt-0.5">{data[hovered]!.longLabel ?? data[hovered]!.label}</p>
          </div>
        )}
      </div>

      <TableDisclosure
        id={tableId}
        open={showTable}
        onToggle={() => setShowTable((v) => !v)}
        headers={['Date', 'Compliance', 'Source']}
        rows={data.map((d) => [
          d.longLabel ?? d.label,
          d.value === null ? '—' : `${d.value}${valueSuffix}`,
          d.seed ? 'Seed (derived)' : 'Measured',
        ])}
      />
    </div>
  );
}
